import { AlertTriangle } from "lucide-react";
import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
	children: ReactNode;
	fallback?: ReactNode;
}

interface State {
	hasError: boolean;
	error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { hasError: false, error: null };
	}

	static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		console.error("ErrorBoundary caught:", error, errorInfo);
	}

	handleReset = () => {
		this.setState({ hasError: false, error: null });
	};

	render() {
		if (this.state.hasError) {
			if (this.props.fallback) {
				return this.props.fallback;
			}
			return (
				<div
					className="flex min-h-[400px] flex-col items-center justify-center gap-4 p-8"
					role="alert"
				>
					<AlertTriangle className="h-12 w-12 text-error" aria-hidden="true" />
					<h2 className="text-xl font-semibold text-primary">Something went wrong</h2>
					<p className="max-w-md text-center text-sm text-secondary">
						{this.state.error?.message ?? "An unexpected error occurred."}
					</p>
					<button
						type="button"
						onClick={this.handleReset}
						className="min-h-[44px] rounded-md bg-accent px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-accent-hover"
					>
						Try Again
					</button>
				</div>
			);
		}

		return this.props.children;
	}
}
